import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useUXPContext } from '@core/context/useUXPContext';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { Dropdown } from '@widget/popups/Dropdown';
import { hasValue } from '@utils';
import PaginationButton from './PaginationButton';
import { SkeletonLoader } from '@widget/loaders';
/**
 * @export
 *
 * Pagination controls — compact prev/next with a page-size dropdown.
 * Renders as a flat set of controls; layout (left/right slots) is handled by the parent.
 *
 * @example
 * ```
 * <PaginationComponent
 *   total={total}
 *   pageSize={pageSize}
 *   page={page}
 *   onPageChange={setPage}
 *   onPageSizeChange={ps => { setPageSize(ps); setPage(1); }}
 * />
 * ```
 */
const PaginationComponent = ({ total, pageSize, page, dataLength = 0, loading, onPageChange, onPageSizeChange, allowPageSizeChange = true, }) => {
    const context = useUXPContext();
    const hasTotalCount = hasValue(total) && total > 0;
    const maxPage = hasTotalCount && pageSize > 0 ? Math.ceil(total / pageSize) : 0;
    const start = dataLength > 0 ? (page - 1) * pageSize + 1 : 0;
    const end = (page - 1) * pageSize + dataLength;
    const pageSizeOptions = [10, 20, 50, 100];
    if (loading) {
        return _jsx(SkeletonLoader, { width: "15rem", height: "1rem" });
    }
    return (_jsxs("div", { className: 'uxpcore_pagination__container', children: [allowPageSizeChange ? (_jsx(Dropdown, { position: "top-left", trigger: _jsx("div", { className: "uxpcore_pagination__count", children: hasTotalCount
                        ? context.$L('uxp-core.text.pagination-counts', { start, end, total })
                        : `${start} - ${end}` }), content: _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_pagination__pagesize-title", children: context.$L('uxp-core.text.records-per-page') }), pageSizeOptions.map(ps => (_jsx("div", { className: `uxpcore_pagination__pagesize${pageSize === ps ? ' uxpcore_pagination__pagesize--active' : ''}`, onClick: () => onPageSizeChange(ps), children: context.$L('uxp-core.text.x-records', { count: ps }) }, ps)))] }) })) : (_jsx("div", { className: "uxpcore_pagination__count", children: hasTotalCount
                    ? context.$L('uxp-core.text.pagination-counts', { start, end, total })
                    : `${start} - ${end}` })), _jsx(PaginationButton, { onClick: () => onPageChange(Math.max(page - 1, 1)), disabled: page === 1, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'chevron-left'] }) }), _jsx(PaginationButton, { onClick: () => onPageChange(page + 1), disabled: hasTotalCount
                    ? page >= maxPage
                    : dataLength < pageSize, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'chevron-right'] }) })] }));
};
export default PaginationComponent;
